import React from 'react';
import { Button } from './ui/button';
import { CheckCircle2, Info } from 'lucide-react';

const SOURCE_LABELS = {
  siret_api: 'Prérempli automatiquement à partir du SIRET.',
  generated: 'Généré automatiquement.',
  imported: 'Importé automatiquement.',
  admin: 'Renseigné par un administrateur.',
};

/**
 * AutoFilledField — Wrapper réutilisable pour les champs auto-remplis.
 * 
 * Affiche un indicateur visuel "À vérifier" pour les champs avec status=pending.
 * Fournit un bouton "Confirmer" et détecte les modifications manuelles.
 * 
 * Props:
 * - fieldName: nom du champ (ex: "raison_sociale")
 * - meta: {source, status} ou null/undefined
 * - onConfirm: (fieldName) => void — appelé au clic sur Confirmer
 * - label: libellé humain du champ (pour accessibilité)
 * - children: le champ Input wrappé
 */
const AutoFilledField = ({ fieldName, meta, onConfirm, label, children }) => {
  if (!meta || meta.status !== 'pending') {
    return children;
  }

  const sourceLabel = SOURCE_LABELS[meta.source] || 'Information récupérée automatiquement.';

  return (
    <div
      className="relative rounded-lg border border-blue-200 bg-blue-50/50 p-3 -mx-1"
      data-testid={`auto-field-${fieldName}`}
    >
      {children}

      <div className="flex items-start justify-between gap-2 mt-2">
        <div className="flex items-start gap-1.5 min-w-0">
          <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-600">
            {sourceLabel} Vérifiez cette information.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 h-7 px-2.5 text-xs border-blue-300 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
          onClick={() => onConfirm(fieldName)}
          aria-label={`Confirmer ${label || fieldName}`}
          data-testid={`confirm-field-${fieldName}`}
        >
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Confirmer
        </Button>
      </div>
    </div>
  );
};

export default AutoFilledField;
